import type { ValidationError } from '@/models/error'
import { trialByPlanIOSPlanName } from '@/services/ab/18369_trial_by_plan'

export function monthlyPlan(platform: 'ios' | 'android'): string {
  if (platform === 'ios') {
    // The is_monthly = true
    const trialByPlan = trialByPlanIOSPlanName(true)
    return trialByPlan ? trialByPlan : 'shortform_monthly'
  }
  return 'plan_monthly'
}

export function annualPlan(platform: 'ios' | 'android'): string {
  if (platform === 'ios') {
    // The is_monthly = false
    const trialByPlan = trialByPlanIOSPlanName(false)
    return trialByPlan ? trialByPlan : 'shortform_annual'
  }
  return 'plan_annual'
}

export function allPlans(platform: 'ios' | 'android'): string[] {
  if (platform === 'android') {
    return ['plan_monthly', 'plan_annual']
  }
  return [
    'shortform_monthly',
    'shortform_annual',
    'shortform_monthly_no_trial',
    'shortform_annual_2_week_trial',
  ]
}

export class UserCancelledError {}

export class GoogleBillingUnavailableError {}

export class PlanChangeFailedError extends Error {
  constructor(error?: string) {
    error =
      error ||
      'We could not change your subscription plan. Please restart the app and try again, or contact support.'
    super(error)
  }
}

// The set of store events (initialization, purchases) that we emit/listen to,
// which enables using a async/await interface.
// See the `appStoreEmitter` docstring for more details.
// This set of events uses the capacitor-plugin-cdv-purchase v13 types.
export type PurchaseEvents = {
  storeInit: undefined
  storeInitError: CdvPurchase.IError[]
  productOrderOwned: CdvPurchase.Product
  productOrderCancelled: CdvPurchase.Product
  productOrderError: [CdvPurchase.IError, CdvPurchase.Product | undefined]
  verificationDenied: ValidationError
  subscriptionUpdatedOwned: undefined
  purchasesUpdated: undefined
}

/**
 * An interface for the global `appStore` object for capacitor-plugin-cdv-purchase v13.
 *
 * Wraps the native plugin logic and provides async/await interface
 * for the calling code.
 */
export interface AppStore {
  // The `store` object provided by capacitor-plugin-cdv-purchase.
  store: CdvPurchase.Store | undefined

  // Enable or disable debug mode.
  debug: boolean

  // Text status of the App Store data (for debugging).
  status: string
  // Error text related to App Store (for debugging).
  error: string

  // Monthly subscription data fetched from AppStore.
  monthly?: CdvPurchase.Product
  // Annual subscription data fetched from AppStore.
  annual?: CdvPurchase.Product

  _initialized: boolean
  _deviceReady: boolean
  _platform?: 'ios' | 'android'

  // Initialize the `appStore` object.
  // `isFreeUser` finishes init when product prices load, without waiting
  // for receiptsVerified. Paid users still wait for that event.
  init(platform: 'ios' | 'android', isFreeUser?: boolean): Promise<void>

  // Inform the AppStore object that native device is ready.
  deviceReady(): void

  // Fetch subscriptions data from AppStore.
  updateProducts(): void

  /**
   * Show the monthly subscription dialog.
   *
   * The process should end either with `productOwned` or `productError`
   * event emitted through `appStoreEmitter`.
   *
   * @param isPlanChange Is this a change from the annual plan to the monthly plan.
   *  Should be set to true only when the plan is changed during an active subscription,
   *  not when resubscribing to another plan after the subscription had already expired.
   */
  subscribeMonthly(isPlanChange: boolean): Promise<CdvPurchase.Product>

  /**
   * Show the annual subscription dialog.
   *
   * The process should end either with `productOwned` or `productError`
   * event emitted through `appStoreEmitter`.
   *
   * @param isPlanChange Is this a change from the monthly plan to the annual plan.
   *  Should be set to true only when the plan is changed during an active subscription,
   *  not when resubscribing to another plan after the subscription had already expired.
   */
  subscribeAnnual(isPlanChange: boolean): Promise<CdvPurchase.Product>

  // Receipt updated handler.
  _onReceiptUpdated(transaction: CdvPurchase.Transaction): void

  // Log or skip debug messages depending on the `debug` flag.
  _debugLog(message: string, data?: any): void
}
